import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowRight, Scale, Shield, FileText, CheckCircle } from 'lucide-react';

const LegalAssistant = () => {
  const features = [
    {
      icon: Scale,
      title: "Professional Guidance",
      description: "Get expert analysis and recommendations tailored to your specific matter."
    },
    {
      icon: FileText,
      title: "Custom Templates",
      description: "Receive professionally crafted documents and templates for your case."
    },
    {
      icon: Shield,
      title: "Confidential & Secure",
      description: "Your information is protected with enterprise-grade security and privacy."
    },
    {
      icon: CheckCircle,
      title: "Actionable Solutions",
      description: "Get step-by-step guidance and clear next steps to resolve your issues."
    }
  ];

  const handleGetStarted = () => {
    // Scroll to pricing section where users can see plans and sign up
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="assistant" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Ready to Resolve Your Matter?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Join thousands of users who have successfully resolved their issues with our AI-powered assistant. Get professional guidance in minutes, not days.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg bg-white/80 backdrop-blur-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Main CTA Section */}
          <div className="text-center">
            <Card className="shadow-2xl border-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
              <CardContent className="p-12">
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4">
                    Start Your Journey Today
                  </h3>
                  <p className="text-blue-100 text-lg mb-8 leading-relaxed">
                    Don't let issues overwhelm you. Get instant access to professional guidance, 
                    custom templates, and step-by-step solutions tailored to your specific situation.
                  </p>
                  
                  <Button
                    size="lg"
                    onClick={handleGetStarted}
                    className="bg-white text-blue-700 hover:bg-gray-100 px-12 py-4 text-xl font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  >
                    Get Started Now
                    <ArrowRight className="ml-3 w-6 h-6" />
                  </Button>
                  
                  <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-blue-100">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>No Credit Card Required</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>100% Confidential</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>Instant Results</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="text-center bg-white/60 backdrop-blur-sm rounded-xl p-6 shadow-lg">
              <div className="text-3xl font-bold text-blue-600 mb-2">800+</div>
              <div className="text-sm text-gray-600">Members</div>
            </div>
            <div className="text-center bg-white/60 backdrop-blur-sm rounded-xl p-6 shadow-lg">
              <div className="text-3xl font-bold text-green-600 mb-2">91%</div>
              <div className="text-sm text-gray-600">Success Rate</div>
            </div>
            <div className="text-center bg-white/60 backdrop-blur-sm rounded-xl p-6 shadow-lg">
              <div className="text-3xl font-bold text-purple-600 mb-2">25+</div>
              <div className="text-sm text-gray-600">Custom Templates</div>
            </div>
            <div className="text-center bg-white/60 backdrop-blur-sm rounded-xl p-6 shadow-lg">
              <div className="text-3xl font-bold text-indigo-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">AI Assistance</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LegalAssistant;